import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Sparkles, Target, Star, Coins, PieChart, Users as UsersIcon, MapPin,
  Calendar as CalIcon, TrendingUp, Award, Clock, Euro, ArrowUpRight, AlertTriangle, Info,
  CheckCircle2, Loader2, ChevronRight, RefreshCw, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import FinancialValue from "@/components/FinancialValue";
import FinancePinShield from "@/components/FinancePinShield";

const ICON_MAP = {
  target: Target,
  star: Star,
  coins: Coins,
  "pie-chart": PieChart,
  users: UsersIcon,
  "map-pin": MapPin,
  calendar: CalIcon,
  sparkles: Sparkles,
};

const fmtEur = (v) => new Intl.NumberFormat("it-IT", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(v || 0);

const fmtNum = (v) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: 1 }).format(v || 0);

export default function AIManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [activeInsight, setActiveInsight] = useState(null);

  const fetchStats = ({ refresh = false } = {}) => {
    if (refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    return api
      .get("/ai-manager/stats", { params: refresh ? { refresh: true } : {} })
      .then((r) => {
        setData(r.data);
        setError("");
        if (refresh) {
          const geo = r.data?.logistics?.geocoded_this_call || 0;
          toast.success(
            geo > 0
              ? `Analisi aggiornata · ${geo} location geolocalizzate`
              : "Analisi aggiornata",
          );
        }
      })
      .catch((e) => {
        const msg = e.response?.data?.detail || "Errore caricamento dati";
        setError(msg);
        if (refresh) toast.error(msg);
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => fetchStats({ refresh: true });

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" strokeWidth={1.5} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white border border-border rounded-sm p-10 text-center">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }
  if (!data) return null;

  const { kpi, economic, time, team, logistics, insights, score, analysis, locked } = data;

  return (
    <div className="space-y-7 relative" data-testid="ai-manager-page">
      {/* Plan-locked overlay (free/basic/pro) — shows demo data behind a soft blur */}
      {locked && <AIManagerLockOverlay />}

      <div className={locked ? "pointer-events-none select-none filter blur-[2px]" : ""}>

      {/* Hero */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-uppercase flex items-center gap-2 text-primary">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Business Intelligence
          </span>
          <h1 className="font-serif-heading text-4xl sm:text-5xl tracking-tight mt-2">
            AI Manager<span className="text-primary">™</span>
          </h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-xl">
            Analisi economica, tempo, team e logistica dei tuoi eventi. Suggerimenti automatici
            per far crescere il tuo business wedding.
          </p>
        </div>
        {!locked && (
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="ai-manager-refresh-top"
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              strokeWidth={1.5}
            />
            <span className="text-sm font-medium">
              {refreshing ? "Aggiornamento…" : "Aggiorna analisi"}
            </span>
          </button>
        )}
      </div>

      {/* Wedding Business Score (hero) */}
      <ScoreCard score={score} />

      {/* Analisi intelligente del business — consultant narrative */}
      <AnalysisCard
        paragraphs={analysis}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      {/* Section 1: KPI base */}
      <SectionHeader title="KPI" subtitle="Indicatori chiave sui preventivi e contratti." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Preventivi inviati" value={kpi.quotes_sent} icon={ArrowUpRight} testid="kpi-quotes-sent" />
        <Kpi label="Preventivi accettati" value={kpi.quotes_accepted} icon={CheckCircle2} testid="kpi-quotes-accepted" />
        <Kpi label="Tasso conversione" value={`${fmtNum(kpi.conversion_rate)}%`} icon={Target} testid="kpi-conversion" />
        <Kpi label="Contratti firmati" value={kpi.contracts_signed} icon={Award} testid="kpi-signed" />
        <Kpi label="Eventi completati" value={kpi.events_completed} icon={CalIcon} testid="kpi-completed" />
        <Kpi label="Preventivi rifiutati" value={kpi.quotes_rejected} icon={AlertTriangle} testid="kpi-rejected" />
        <Kpi label="Clienti totali" value={kpi.total_clients} icon={UsersIcon} testid="kpi-total-clients" />
      </div>

      {/* Section 2: Economic */}
      <div className="flex items-start justify-between gap-3">
        <SectionHeader title="Performance economica" subtitle="Revenue reale, potenziale e previsione." />
        <div className="pt-1"><FinancePinShield /></div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Fatturato generato" value={<FinancialValue value={fmtEur(economic.revenue_generated)} testid="econ-revenue-value" />} icon={Euro} accent testid="econ-revenue" />
        <Kpi label="Fatturato potenziale" value={<FinancialValue value={fmtEur(economic.revenue_potential)} testid="econ-potential-value" />} icon={TrendingUp} testid="econ-potential" />
        <Kpi label="Valore medio sposa" value={<FinancialValue value={fmtEur(economic.avg_bride_value)} testid="econ-avg-bride-value" />} icon={Star} testid="econ-avg-bride" />
        <Kpi label="Previsione entrate" value={<FinancialValue value={fmtEur(economic.revenue_forecast)} testid="econ-forecast-value" />} icon={TrendingUp} accent testid="econ-forecast" />
      </div>

      {/* Section 2b: Eventi completati — riepilogo economico.
          Distingue chiaramente incasso reale (deposits registrati) da
          chiusura amministrativa (manually_settled). MAI sommati. */}
      <CompletedEventsCard summary={data.completed_events_summary} />

      {/* Section 3: Time & profitability */}
      <SectionHeader title="Tempo & redditività" subtitle="Ore lavorate e guadagno orario dai dati timeline." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Ore lavorate totali" value={`${fmtNum(time.total_hours)}h`} icon={Clock} testid="time-total" />
        <Kpi label="Ore medie per evento" value={`${fmtNum(time.hours_avg)}h`} icon={Clock} testid="time-avg" />
        <Kpi label="Guadagno orario medio" value={<FinancialValue value={fmtEur(time.hourly_avg)} testid="time-hourly-value" />} icon={Euro} accent testid="time-hourly" />
        <Kpi label="Eventi poco profittevoli" value={time.low_profit_count} icon={AlertTriangle} testid="time-lowprofit" />
      </div>
      {time.best_event && (
        <div className="bg-white border border-border rounded-sm p-5 flex items-center gap-4" data-testid="best-event-card">
          <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Star className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="label-uppercase text-primary">Evento più profittevole</div>
            <div className="font-serif-heading text-xl mt-1 truncate">{time.best_event.name}</div>
            <div className="text-xs text-neutral-500 mt-1">
              <FinancialValue value={fmtEur(time.best_event.amount)} /> · {fmtNum(time.best_event.hours)}h ·{" "}
              <FinancialValue value={fmtEur(time.best_event.per_hour)} /> / h
            </div>
          </div>
          <Link
            to={`/app/clients/${time.best_event.client_id}`}
            className="text-primary hover:underline text-xs"
            data-testid="best-event-open"
          >
            Apri scheda →
          </Link>
        </div>
      )}

      {/* Section 4: Team */}
      <SectionHeader title="Team" subtitle="Utilizzo del team ed efficienza." />
      <div className="grid sm:grid-cols-3 gap-4">
        <Kpi label="Media operatori per evento" value={fmtNum(team.avg_operators)} icon={UsersIcon} testid="team-avg-ops" />
        <Kpi label="Media assistenti per evento" value={fmtNum(team.avg_assistants)} icon={UsersIcon} testid="team-avg-assist" />
        <Kpi label="Efficienza team" value={<FinancialValue value={fmtEur(team.efficiency)} testid="team-efficiency-value" />} icon={TrendingUp} accent testid="team-efficiency" />
      </div>

      {/* Section 5: Logistics */}
      <SectionHeader title="Logistica" subtitle="Distanze tra il tuo punto di partenza e le location eventi." />
      {!logistics.has_starting_point ? (
        <div className="bg-white border border-border rounded-sm p-6 flex items-start gap-3" data-testid="no-starting-point">
          <MapPin className="w-5 h-5 text-primary mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="font-medium">Nessun punto di partenza configurato</div>
            <p className="text-sm text-neutral-600 mt-1">
              Imposta il tuo indirizzo operativo per sbloccare l'analisi logistica.{" "}
              <Link to="/app/settings" className="text-primary hover:underline">
                Vai alle Impostazioni →
              </Link>
            </p>
          </div>
        </div>
      ) : logistics.events_with_km === 0 ? (
        <div className="bg-white border border-border rounded-sm p-6 text-sm text-neutral-600" data-testid="no-logistics">
          Nessuna location evento è ancora geolocalizzata. I km verranno calcolati automaticamente
          mentre carichi nuovi eventi.
        </div>
      ) : (
        <div className="grid sm:grid-cols-3 gap-4">
          <Kpi label="Km totali" value={`${fmtNum(logistics.total_km)} km`} icon={MapPin} testid="logistics-total" />
          <Kpi label="Km medi per evento" value={`${fmtNum(logistics.avg_km)} km`} icon={MapPin} testid="logistics-avg" />
          <Kpi label="Eventi con km calcolati" value={`${logistics.events_with_km} / ${logistics.total_signed}`} icon={CalIcon} testid="logistics-count" />
        </div>
      )}

      {/* Unreliable-distance warning: some venues have implausible km (>200) or too-generic address. */}
      {logistics.has_starting_point &&
        ((logistics.invalid_km_count || 0) > 0 || (logistics.skipped_generic_count || 0) > 0) && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-sm p-5 flex items-start gap-3"
          data-testid="logistics-warning"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.5} />
          <div className="text-sm text-amber-900">
            <div className="font-medium">Impossibile calcolare correttamente la distanza</div>
            <p className="mt-1 opacity-90 leading-relaxed">
              {(logistics.invalid_km_count || 0) > 0 && (
                <>
                  {logistics.invalid_km_count} location producono distanze non attendibili
                  (oltre {logistics.max_valid_km || 200} km).{" "}
                </>
              )}
              {(logistics.skipped_generic_count || 0) > 0 && (
                <>
                  {logistics.skipped_generic_count} indirizzi sono troppo generici per essere
                  geolocalizzati con precisione.{" "}
                </>
              )}
              Verifica i campi <span className="font-medium">location/venue</span> dei tuoi clienti
              (inserisci via e città — es. "Villa Giulia, Roma"). Gli eventi con dato inattendibile
              sono esclusi dall'analisi.
            </p>
          </div>
        </div>
      )}

      {/* Section 6: AI Insights */}
      <SectionHeader title="AI Insights" subtitle="Suggerimenti dinamici basati sui tuoi dati. Clicca per agire." />
      <div className="space-y-3" data-testid="ai-insights-list">
        {insights.map((i, idx) => {
          const Icon = ICON_MAP[i.icon] || Info;
          const toneMap = {
            good: "bg-emerald-50 border-emerald-200 text-emerald-900",
            warn: "bg-amber-50 border-amber-200 text-amber-900",
            info: "bg-primary/5 border-primary/20 text-neutral-800",
          };
          const iconColor = {
            good: "text-emerald-600",
            warn: "text-amber-600",
            info: "text-primary",
          };
          const hasCta = !!i.cta;
          const onClick = hasCta ? () => setActiveInsight(i) : undefined;
          const Tag = hasCta ? "button" : "div";
          return (
            <Tag
              key={idx}
              onClick={onClick}
              type={hasCta ? "button" : undefined}
              className={`w-full text-left border rounded-sm p-5 flex items-start gap-4 ${toneMap[i.severity] || toneMap.info} ${hasCta ? "hover:shadow-sm cursor-pointer transition-shadow" : ""}`}
              data-testid={`insight-${idx}`}
            >
              <span className="w-9 h-9 rounded-full bg-white border border-current/10 flex items-center justify-center shrink-0">
                <Icon className={`w-4 h-4 ${iconColor[i.severity] || iconColor.info}`} strokeWidth={1.5} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{i.title}</div>
                <p className="text-sm mt-1 opacity-90 leading-relaxed">{i.message}</p>
                {hasCta && (
                  <span className="inline-flex items-center gap-1 text-xs mt-2 font-medium opacity-95">
                    {i.cta.label}
                    <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </span>
                )}
              </div>
            </Tag>
          );
        })}
      </div>

      <InsightDialog insight={activeInsight} onClose={() => setActiveInsight(null)} />
      </div>
    </div>
  );
}

// Lock overlay shown to users without the AI_MANAGER plan. The page underneath
// shows DEMO data (fake KPIs) blurred — never real user data.
function AIManagerLockOverlay() {
  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center px-4 -m-4"
      data-testid="ai-manager-lock-overlay"
    >
      {/* Soft white scrim so demo numbers stay teasingly visible behind */}
      <div className="absolute inset-0 bg-white/55 backdrop-blur-[1px]" />

      <div className="relative bg-white border-2 border-[#1a1a1a] rounded-sm p-7 max-w-md text-center shadow-xl pointer-events-auto">
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1a1a1a] text-white text-[10px] tracking-widest uppercase px-3 py-1 rounded-sm">
          Anteprima AI Manager™
        </span>

        <div className="w-12 h-12 mx-auto rounded-full bg-[#1a1a1a]/10 flex items-center justify-center mt-2">
          <Sparkles className="w-5 h-5 text-[#1a1a1a]" strokeWidth={1.5} />
        </div>

        <h3 className="font-serif-heading text-2xl mt-3 leading-tight">
          Sblocca AI Manager™
        </h3>
        <p className="text-sm text-neutral-600 mt-3 leading-relaxed">
          Sblocca AI Manager™ per analizzare e ottimizzare il tuo business wedding —
          fatturato, conversione, redditività, team e logistica con suggerimenti
          azionabili dal tuo consulente virtuale.
        </p>

        <Link
          to="/app/plans"
          className="inline-flex items-center justify-center gap-2 mt-5 px-5 py-2.5 rounded-sm bg-[#1a1a1a] hover:bg-black text-white text-sm font-medium transition-colors"
          data-testid="ai-manager-lock-cta"
        >
          <Sparkles className="w-4 h-4" strokeWidth={1.5} />
          Passa a AI Manager™
        </Link>

        <p className="text-[10px] tracking-wider uppercase text-neutral-400 mt-4">
          I dati mostrati sono di esempio
        </p>
      </div>
    </div>
  );
}

// --- Small helpers ---

function SectionHeader({ title, subtitle }) {
  return (
    <div className="pt-4">
      <h2 className="font-serif-heading text-2xl tracking-tight">{title}</h2>
      {subtitle && <p className="text-sm text-neutral-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent = false, testid }) {
  return (
    <div
      className={`border rounded-sm p-5 ${accent ? "bg-primary/5 border-primary/20" : "bg-white border-border"}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="label-uppercase">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />}
      </div>
      <div className="font-serif-heading text-3xl sm:text-4xl mt-3 tracking-tight">
        {value}
      </div>
    </div>
  );
}

// Eventi completati — riepilogo economico.
// Mostra il quadro reale del cash su eventi già conclusi, separando in
// modo netto INCASSO REALE (deposits registrati) da CHIUSURA AMMINISTRATIVA
// (quote.manually_settled). Le due cose non vengono mai sommate, per non
// inquinare la lettura della performance economica.
function CompletedEventsCard({ summary }) {
  if (!summary || !summary.count) {
    return (
      <div
        className="bg-white border border-border rounded-sm p-5 text-center text-sm text-neutral-500"
        data-testid="completed-events-empty"
      >
        Nessun evento completato ancora — il riepilogo apparirà qui dopo il primo matrimonio.
      </div>
    );
  }
  const rows = [
    {
      label: "Eventi completati",
      value: summary.count,
      sub: "Wedding già conclusi",
      testid: "completed-count",
      tone: "neutral",
    },
    {
      label: "Già maturato",
      value: <FinancialValue value={fmtEur(summary.total_quoted)} testid="completed-quoted-value" />,
      sub: "Totale preventivato",
      testid: "completed-quoted",
      tone: "neutral",
    },
    {
      label: "Incassato registrato",
      value: <FinancialValue value={fmtEur(summary.total_collected)} testid="completed-collected-value" />,
      sub: "Solo acconti reali",
      testid: "completed-collected",
      tone: "positive",
    },
    {
      label: "Residuo aperto",
      value: <FinancialValue value={fmtEur(summary.total_open)} testid="completed-open-value" />,
      sub: "Ancora da incassare",
      testid: "completed-open",
      tone: summary.total_open > 0 ? "warning" : "neutral",
    },
    {
      label: "Saldati manualmente",
      value: <FinancialValue value={fmtEur(summary.total_manually_settled)} testid="completed-settled-value" />,
      sub: "Chiusura amministrativa · NON incasso",
      testid: "completed-settled",
      tone: "info",
    },
  ];
  const toneClasses = {
    neutral: "bg-white border-border",
    positive: "bg-emerald-50/40 border-emerald-200",
    warning: "bg-amber-50/40 border-amber-200",
    info: "bg-sky-50/40 border-sky-200",
  };
  return (
    <div className="space-y-3" data-testid="completed-events-card">
      <div className="flex items-baseline justify-between gap-2 px-1">
        <h3 className="font-serif-heading text-xl tracking-tight">
          Eventi completati
        </h3>
        <span className="text-xs text-neutral-500">
          Incasso reale e chiusure manuali sono tenuti separati.
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {rows.map((r) => (
          <div
            key={r.testid}
            className={`border rounded-sm p-4 ${toneClasses[r.tone]}`}
            data-testid={r.testid}
          >
            <div className="label-uppercase text-[10px]">{r.label}</div>
            <div className="font-serif-heading text-xl sm:text-2xl mt-2 tabular-nums tracking-tight">
              {r.value}
            </div>
            <div className="text-[11px] text-neutral-500 mt-1.5 leading-tight">{r.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScoreCard({ score }) {
  const pct = Math.min(100, Math.max(0, score?.value || 0));
  const color =
    pct >= 80 ? "#059669"
    : pct >= 60 ? "#C8A97E"
    : pct >= 40 ? "#D97706"
    : "#DC2626";
  const R = 54;
  const C = 2 * Math.PI * R;
  return (
    <div className="bg-white border border-border rounded-sm p-6 sm:p-8" data-testid="wbs-score-card">
      <div className="grid sm:grid-cols-[140px_1fr] gap-6 items-center">
        <div className="relative w-[140px] h-[140px] mx-auto sm:mx-0">
          <svg width="140" height="140" viewBox="0 0 140 140">
            <circle cx="70" cy="70" r={R} fill="none" stroke="#EAE7E0" strokeWidth="10" />
            <circle
              cx="70" cy="70" r={R}
              fill="none"
              stroke={color}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={C - (C * pct) / 100}
              transform="rotate(-90 70 70)"
              style={{ transition: "stroke-dashoffset 600ms ease-out" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-serif-heading text-4xl" data-testid="wbs-score-value">{fmtNum(pct)}</div>
            <div className="text-[10px] tracking-widest uppercase text-neutral-400">/ 100</div>
          </div>
        </div>
        <div>
          <span className="label-uppercase text-primary">Wedding Business Score</span>
          <h2 className="font-serif-heading text-3xl mt-2 tracking-tight">
            {pct >= 80 ? "Business solido"
             : pct >= 60 ? "Business sano"
             : pct >= 40 ? "In costruzione"
             : "Da migliorare"}
          </h2>
          <p className="text-sm text-neutral-600 mt-2">{score?.interpretation}</p>
          <div className="grid grid-cols-4 gap-2 mt-4 text-xs" data-testid="wbs-components">
            <ScorePill label="Conversione" value={score?.components?.conversion} />
            <ScorePill label="Orario" value={score?.components?.hourly} />
            <ScorePill label="Team" value={score?.components?.team} />
            <ScorePill label="Logistica" value={score?.components?.logistics} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ScorePill({ label, value }) {
  return (
    <div className="bg-secondary/50 border border-border rounded-sm px-3 py-2 text-center">
      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</div>
      <div className="font-medium tabular-nums text-neutral-800 mt-0.5">{fmtNum(value || 0)}</div>
    </div>
  );
}

// Consultant-style narrative built from real KPIs. Paragraphs come pre-formatted
// from the backend (5-6 blocks: Andamento, Performance, Organizzazione, Team,
// Logistica, Consiglio finale). Includes a manual refresh button.
function AnalysisCard({ paragraphs = [], onRefresh, refreshing }) {
  const items = Array.isArray(paragraphs) ? paragraphs : [];
  return (
    <section
      className="bg-gradient-to-br from-primary/5 via-white to-white border border-primary/20 rounded-sm p-6 sm:p-8"
      data-testid="ai-analysis-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </span>
          <div>
            <span className="label-uppercase text-primary">Analisi del consulente</span>
            <h2 className="font-serif-heading text-2xl sm:text-3xl tracking-tight mt-1">
              Analisi intelligente del business
            </h2>
            <p className="text-sm text-neutral-500 mt-1 max-w-2xl">
              Una lettura consulenziale dei tuoi numeri reali — come se un advisor
              esperto di wedding business stesse guardando insieme a te i dati di oggi.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-sm border border-primary/30 bg-white text-primary hover:bg-primary/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="ai-analysis-refresh"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
            strokeWidth={1.5}
          />
          <span className="text-xs font-medium">
            {refreshing ? "Aggiornamento…" : "Aggiorna analisi"}
          </span>
        </button>
      </div>

      {items.length === 0 ? (
        <div
          className="mt-6 bg-white/70 border border-border/60 rounded-sm p-6 text-sm text-neutral-600"
          data-testid="ai-analysis-empty"
        >
          Non ho ancora dati sufficienti per un'analisi completa. Registra i primi
          preventivi e contratti: l'AI Manager inizierà a tracciare le tendenze del
          tuo business.
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:gap-5">
          {items.map((p, idx) => (
            <article
              key={idx}
              className="bg-white border border-border rounded-sm p-5 sm:p-6"
              data-testid={`ai-analysis-paragraph-${idx}`}
            >
              <div className="flex items-baseline gap-3">
                <span className="font-serif-heading text-primary text-xl tabular-nums">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif-heading text-xl tracking-tight text-neutral-900">
                  {p.title}
                </h3>
              </div>
              <p className="text-[15px] leading-relaxed text-neutral-700 mt-3">
                {p.text}
              </p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

// Dialog that renders the list of items attached to an insight's CTA.
// Each row is clickable (links to client detail) when a client_id is present.
function InsightDialog({ insight, onClose }) {
  const open = !!insight;
  const cta = insight?.cta;
  const items = cta?.items || [];
  const columns = cta?.columns || [];

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  const renderCell = (item, col) => {
    const c = col.toLowerCase();
    if (c === "cliente") return item.name || "—";
    if (c === "importo") return <FinancialValue value={fmtEur(item.amount || 0)} />;
    if (c === "ore") return `${fmtNum(item.hours || 0)}h`;
    if (c === "€/h") return <FinancialValue value={fmtEur(item.per_hour || 0)} />;
    if (c === "operatori") return item.operators ?? "—";
    if (c === "km") return `${fmtNum(item.km || 0)} km`;
    if (c === "inviato") return fmtDate(item.sent_at);
    if (c === "data") return fmtDate(item.date);
    if (c === "eventi") return item.events_count ?? "—";
    if (c === "ore totali") return `${fmtNum(item.total_hours || 0)}h`;
    if (c === "clienti") return (item.clients || []).join(", ") || "—";
    return "—";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="insight-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-heading text-2xl">
            {cta?.label || insight?.title}
          </DialogTitle>
          <DialogDescription>{insight?.message}</DialogDescription>
        </DialogHeader>

        {items.length === 0 ? (
          <div className="py-8 text-center text-sm text-neutral-500" data-testid="insight-dialog-empty">
            {cta?.empty_text || "Nessun elemento da mostrare."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {columns.map((c) => (
                    <th key={c} className="text-left py-2 pr-4 label-uppercase text-neutral-500 font-normal">{c}</th>
                  ))}
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const hasClient = !!item.client_id;
                  const RowTag = hasClient ? Link : "tr";
                  return hasClient ? (
                    <tr
                      key={idx}
                      className="border-b border-border/50 last:border-0 hover:bg-secondary/30 transition-colors"
                      data-testid={`insight-row-${idx}`}
                    >
                      {columns.map((col) => (
                        <td key={col} className="py-3 pr-4 tabular-nums">{renderCell(item, col)}</td>
                      ))}
                      <td className="text-right pr-1">
                        <Link
                          to={`/app/clients/${item.client_id}`}
                          className="text-primary hover:underline inline-flex items-center gap-1 text-xs"
                          onClick={onClose}
                        >
                          Apri <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Link>
                      </td>
                    </tr>
                  ) : (
                    <tr key={idx} className="border-b border-border/50 last:border-0" data-testid={`insight-row-${idx}`}>
                      {columns.map((col) => (
                        <td key={col} className="py-3 pr-4 tabular-nums">{renderCell(item, col)}</td>
                      ))}
                      <td></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
